import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

// Cache para evitar múltiples llamadas al mismo país
const banksCache = {};

/**
 * Hook para obtener la lista de bancos por país desde el backend
 * @param {string} country - País para obtener bancos (Bolivia, Chile, Argentina, etc.)
 * @returns {{ banks: string[], loading: boolean, error: string|null, refetch: function }}
 */
export const useBanks = (country) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBanks = useCallback(async () => {
    if (!country) {
      setBanks([]);
      return;
    }

    // Verificar cache primero
    if (banksCache[country]) {
      setBanks(banksCache[country]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/banks/${encodeURIComponent(country)}`);
      const bankList = response.data.banks || [];
      
      // Guardar en cache
      banksCache[country] = bankList;
      setBanks(bankList);
    } catch (err) {
      console.error(`Error fetching banks for ${country}:`, err);
      setError(err.response?.data?.detail || 'Error al cargar bancos');
      setBanks([]);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  return { banks, loading, error, refetch: fetchBanks };
};

/**
 * Función para obtener bancos de forma imperativa (sin hook)
 * Útil para usar fuera de componentes React
 * @param {string} country - País para obtener bancos
 * @returns {Promise<string[]>}
 */
export const fetchBanksByCountry = async (country) => {
  if (!country) return [];
  
  // Verificar cache primero
  if (banksCache[country]) {
    return banksCache[country];
  }

  try {
    const response = await api.get(`/banks/${encodeURIComponent(country)}`);
    const bankList = response.data.banks || [];
    banksCache[country] = bankList;
    return bankList;
  } catch (err) {
    console.error(`Error fetching banks for ${country}:`, err);
    return [];
  }
};

/**
 * Limpiar cache de bancos (útil si se actualizan desde admin)
 */
export const clearBanksCache = () => {
  Object.keys(banksCache).forEach(key => delete banksCache[key]);
};

export default useBanks;
